//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Grid, Typography, Box } from '@mui/material'
import React from 'react'
import { EthAddr } from '@shared/SchemaTypes'
import {
  PoolBaseAccountCTO,
  PoolBaseAccountCTOSchema,
  PoolBaseOverviewCTO,
  PoolFlexAccountCTO,
  PoolFlexAccountCTOSchema,
} from '@shared/sharedTypes'
import ViewField from '@react/components/elements/ViewField'
import { SectionHeader } from '@react/components/common/SectionHeader'
import { REPORT_FETCH_DESCRIPTOR_TRANSACTION_LIST } from '@react/reporting/ReportDescriptors'
import ReportRowGenerator from '@react/reporting/ReportRowGenerator'
import PoolHoldingsSkeleton from '@react/components/skeletons/PoolHoldingsSkeleton'

interface FlexPoolHoldingsProps {
  poolFlexAccountCTO: PoolFlexAccountCTO
  poolBaseAccountCTO: PoolBaseAccountCTO
  poolBaseOverviewCTO: PoolBaseOverviewCTO
  poolAddr: EthAddr
  accountAddr: EthAddr
}

export default function FlexPoolHoldings({
  poolFlexAccountCTO,
  poolBaseAccountCTO,
  poolBaseOverviewCTO,
  poolAddr,
  accountAddr,
}: FlexPoolHoldingsProps): React.ReactElement {
  // Add loading state check
  if (!poolFlexAccountCTO || !poolBaseAccountCTO || !poolBaseOverviewCTO) {
    return <PoolHoldingsSkeleton />
  }

  return (
    <Box>
      <Box px={3} py={2} sx={{ flexGrow: 1 }}>
        <SectionHeader>
          <Typography variant={'captionBold'}>My Investment Details</Typography>
        </SectionHeader>

        <Grid container spacing={8} sx={{ padding: '24px', marginTop: '-24px', marginLeft: '0px' }}>
          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolFlexAccountCTO}
              fieldSchema={PoolFlexAccountCTOSchema.describe().fields.principalEarningInterest}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolFlexAccountCTO}
              fieldSchema={PoolFlexAccountCTOSchema.describe().fields.interestAccrued}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolBaseAccountCTO}
              poolTokenSymbol={poolBaseOverviewCTO.symbol}
              fieldSchema={PoolBaseAccountCTOSchema.describe().fields.tokenBalance}
              toolTip="The number of vault tokens you currently hold."
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolFlexAccountCTO}
              fieldSchema={PoolFlexAccountCTOSchema.describe().fields.assetsDeposited}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
            />
          </Grid>
        </Grid>

        <SectionHeader>
          <Typography variant={'captionBold'}>Vault Transactions</Typography>
        </SectionHeader>

        <Grid container spacing={8} sx={{ padding: '24px', marginTop: '-24px', marginLeft: '0px' }}>
          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolFlexAccountCTO}
              poolTokenSymbol={poolBaseOverviewCTO.symbol}
              fieldSchema={PoolFlexAccountCTOSchema.describe().fields.requestedAssetsOf}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolFlexAccountCTO}
              poolTokenSymbol={poolBaseOverviewCTO.symbol}
              fieldSchema={PoolFlexAccountCTOSchema.describe().fields.maxWithdrawRequest}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolFlexAccountCTO}
              poolTokenSymbol={poolBaseOverviewCTO.symbol}
              fieldSchema={PoolFlexAccountCTOSchema.describe().fields.assetsDueForWithdraws}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ViewField
              dataObject={poolFlexAccountCTO}
              poolTokenSymbol={poolBaseOverviewCTO.symbol}
              fieldSchema={PoolFlexAccountCTOSchema.describe().fields.assetsWithdrawn}
              liquidityTokenSymbol={poolBaseOverviewCTO.liquidityTokenSymbol}
            />
          </Grid>
        </Grid>

        <Box sx={{ marginTop: 2 }}>
          <ReportRowGenerator
            reportDescriptor={REPORT_FETCH_DESCRIPTOR_TRANSACTION_LIST}
            initialLoadWithoutRefresh={true}
            poolAddr={poolAddr}
            lenderAddr={accountAddr}
            numberOfDays={30}
          />
        </Box>
      </Box>
    </Box>
  )
}
